<template>
    <span
        v-if="hasHoverPointer"
        v-bind="$attrs"
        v-tooltip:[tooltipArg]="tooltipOptions"
        class="info-icon"
        :class="{ warning: variantToUse === 'warning' }"
    >
        <font-awesome-icon :icon="iconToUse" />
    </span>
    <button
        v-else
        v-bind="$attrs"
        type="button"
        v-tooltip:[tooltipArg]="tooltipOptions"
        class="info-icon button"
        :class="{ warning: variantToUse === 'warning' }"
        :aria-label="props.tooltipText"
        @click.stop
    >
        <font-awesome-icon :icon="iconToUse" />
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { getTooltipDirectiveArg, Position } from '@/types/general/Direction.ts';

type Variant = 'questionCircle' | 'info' | 'warning' | 'infoCircle';

const DELAY_TIMEOUT = 1000;
const DEFAULT_DIRECTION = 'right';

const VARIANT_ICON_MAP: Record<Variant, [string, string]> = {
    questionCircle: ['fad', 'question-circle'],
    info: ['fas', 'info'],
    warning: ['fad', 'exclamation-circle'],
    infoCircle: ['fas', 'info-circle'],
};

defineOptions({
    inheritAttrs: false,
});

const props = defineProps<{
    tooltipText: string;
    direction?: Position;
    delayed?: boolean;
    variant?: Variant;
}>();

const hasHoverPointer = useMediaQuery('(hover: hover) and (pointer: fine)');

const variantToUse = computed<Variant>(() => props.variant ?? 'info');
const iconToUse = computed<[string, string]>(() => VARIANT_ICON_MAP[variantToUse.value]);
const tooltipPosition = computed<Position>(() => props.direction ?? DEFAULT_DIRECTION);

const tooltipArg = computed(() => getTooltipDirectiveArg(
    tooltipPosition.value,
    !hasHoverPointer.value,
));

const tooltipOptions = computed(() => ({
    value: props.tooltipText,
    escape: false,
    showDelay: props.delayed ? DELAY_TIMEOUT : 0,
}));

</script>

<style lang="scss" scoped>

.info-icon {
    cursor: help;
    padding: var(--smallSpacing);

    &.button {
        border: none;
        background: transparent;
        cursor: pointer;
        font: inherit;
        line-height: inherit;
    }

    &.warning svg {
        --fa-primary-color: var(--neutral0);
        --fa-secondary-color: var(--lcpWatermelon);
        --fa-secondary-opacity: 1;
    }
}

</style>
